import { GTag } from "./GoogleAnalytics";
import { TelemetryService } from "../../../src/services/TelemetryService";
import { debounce } from "lodash";
import { AddAssetAction } from "../../entities/AddAssetAction";

export enum Location {
	Library = "Library",
	BuilderPreview = "BuilderPreview"
}

export enum LibraryLocation {
	Calendar = "Calendar",
	ListView = "ListView",
	QuickAccess = "Quick Access",
}

export enum EntityDetails {
	Scripture = "Scripture",
	Location = "Location",
	PreachDate = "Preach Date",
	Name = "Name",
}

export enum SearchEntity {
	Sermon = "Sermon",
	Illustration = "Illustration",
	Bible = "Bible",
	Genius = "Sermon Genius",
}

export type OfflineActionLocation =
	| "Drawer Toggle (Library)"
	| "Drawer Icon (Library)"
	| "Drawer Toggle (Builder)"
	| "Drawer Icon (Builder)"
	| "App Bar Icon (Builder)"
	| "App Bar Icon Mobile (Builder)"
	| "File Dropdown (Builder)"
	| "3 Dots Mobile (Builder)"
	| "Right Click (Library)"
	| "3 Dots Desktop (Library)"
	| "3 Dots Mobile (Library)"; 

export type BuilderActionLocations =
	| "Navigation Desktop" 
	| "Navigation Mobile"
	| "Detail Drawer Interaction"
	| "Slide Drawer Interaction"
	| "Bible Drawer Interaction"
	| "Illustration Drawer Interaction"
	| "Sermon Drawer Interaction"
	| "Genius Drawer Interaction"
	| "Analyzer Drawer Interaction"

export type PremiumContentLocations =
	| "Premium Series"
	| "Premium Sermon"
	
export type PrintActions = 
	"Quick Print"
	| "Single Column"
	| "Two Column"
	| "Three Column"

export class EventTracking {
	static Offline = {
		enableOffline(actionLocation: OfflineActionLocation, offlineAssetCount: number) {
			GTag.event(actionLocation, { event_category: "Enable Offline", event_label: offlineAssetCount.toString() });
		},
		disableOffline(actionLocation: OfflineActionLocation, offlineAssetCount: number) {
            GTag.event(actionLocation, { event_category: "Disable Offline", event_label: offlineAssetCount.toString() });
        },
		conflictResolved(conflictsResolved: number) {
            GTag.event("Resolved Conflicts", { event_category: "Offline", event_label: conflictsResolved.toString() });
        },
	};

	static Subscribe = {
		onNewSubscription(price: number, upgradeAction?: AddAssetAction, fromRegisteration?: boolean) {
			const planName = price === 179 ? "Sermon Maker - Annual" : "Sermon Maker - Monthly"
			GTag.purchase(price, planName);

			if (!upgradeAction) {
				return;
			}

			if (upgradeAction === AddAssetAction.AddDocUpload) {
				GTag.event("Word CTA", { event_category: "Upgrade", event_label: price.toString() });
			} else if (upgradeAction === AddAssetAction.AddSoundCloudLink) {
				GTag.event("SoundCloud CTA", { event_category: "Upgrade", event_label: price.toString() });
			} else if (upgradeAction === AddAssetAction.ImportSermonFromSC) {
				GTag.event("BuilderPreview CTA", { event_category: "Upgrade", event_label: price.toString() });
			} else if (upgradeAction === AddAssetAction.AddVimeoLink) {
				GTag.event("Vimeo CTA", { event_category: "Upgrade", event_label: price.toString() });
			} else if (upgradeAction === AddAssetAction.AddYoutubeLink) {
				GTag.event("YouTube CTA", { event_category: "Upgrade", event_label: price.toString() });
			} else if (upgradeAction === AddAssetAction.ShareAsset) {
				GTag.event("Share CTA", { event_category: "Upgrade", event_label: price.toString() });
			} else if (upgradeAction === AddAssetAction.CreateFromPremiumTemplate || upgradeAction === AddAssetAction.CreateCustomTemplate) {
				GTag.event("Template CTA", { event_category: "Upgrade", event_label: price.toString() });
			} else if (fromRegisteration) {
				GTag.event("Registration", { event_category: "Upgrade", event_label: price.toString() });
			}
		},

		onDiscountPlan(){
			GTag.event("Settings", {event_category: "Discount", event_label: "Converted to Annual Discound Plan" })
		},

		onCancelSubscription(){
			GTag.event("Settings", {event_category: "Cancel", event_label: `Cancelled Subscription` })
		},

		onOpenUpgrade(upgradeAction?: AddAssetAction) {
			if (!upgradeAction) {
				return;
			}

			if (upgradeAction === AddAssetAction.AddDocUpload) {
				GTag.event("Word CTA", { event_category: "Upgrade", event_label: "Intent" });
			} else if (upgradeAction === AddAssetAction.AddSoundCloudLink) {
				GTag.event("SoundCloud CTA", { event_category: "Upgrade", event_label: "Intent" });
			} else if (upgradeAction === AddAssetAction.AddVimeoLink) {
				GTag.event("Vimeo CTA", { event_category: "Upgrade", event_label: "Intent" });
			} else if (upgradeAction === AddAssetAction.AddYoutubeLink) {
				GTag.event("YouTube CTA", { event_category: "Upgrade", event_label: "Intent" });
			} else if (upgradeAction === AddAssetAction.ShareAsset) {
				GTag.event("Share CTA", { event_category: "Upgrade", event_label: "Intent" });
			} else if (
				upgradeAction === AddAssetAction.CreateFromPremiumTemplate ||
				upgradeAction === AddAssetAction.CreateCustomTemplate ||
				upgradeAction === AddAssetAction.Template
			) {
				GTag.event("Template CTA", { event_category: "Upgrade", event_label: "Intent" });
			}
		},
	};

	static Navigation = {
		onSignOut(location: Location) {
			GTag.event("Navigation", { event_category: location, event_label: "Sign Out" });
		},
		onVisitAccount(location: Location) {
			GTag.event("Navigation", { event_category: location, event_label: "Visit Account" });
		},
	};

	static Authentication = {
		onNewRegistration(location: Location) {
			GTag.event("Authentication", { event_category: location, event_label: "New Registration" });
		},
		onLogin(location: Location) {
			GTag.event("Authentication", { event_category: location, event_label: "Login" });
		},
	};

	static Builder = {
		onSaveAsset(detail: EntityDetails) {
			GTag.event("Detail Drawer Interaction", { event_category: "Library", event_label: `Modified ${detail}` });
		},

		onImportSCSermon(sermonId:number){
			GTag.event("Sermon Preview", { event_category: "Builder", event_label: `Sermon ${sermonId} import from SC`});
		},

		onDrawerOpen(drawer: string) {
			GTag.event("Detail Drawer Interaction", { event_category: "Builder", event_label: `${drawer} Opened` });
		},

		onTabChange(drawer: string) {
			GTag.event("Explore Drawer Interaction", { event_category: "Builder", event_label: `${drawer} Tab` });
		},

		onPreferencesChanges(drawer: string, label: string) {
			GTag.event(`${drawer} Settings`, { event_category: "Builder", event_label: label });
		},

		onDownloadSermonAsPdf(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Download Sermon Pdf" });
		},

		onCustomTemplate(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Save as Custom Template" });
		},

		onShare(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Share Asset" });
		},

		onDownloadSermonAsDocx(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Download Sermon Docx" });
		},

		onPrint(action: PrintActions, location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: `Print - ${action}` });
		},

		onExportSlides(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Export Slides" });
		},

		onCloseMainDrawerFromX() {
			GTag.event("Detail Drawer Interaction", { event_category: "Builder", event_label: "Close from X" });
		},

		onPresentSermon(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Present Asset" });
		},

		onPresentSlides(location:BuilderActionLocations){
			GTag.event(location, { event_category: "Builder", event_label: "Present Slides"})
		},

		contentDrawerOpen(entity: SearchEntity, location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: `${entity} Bible` });
		},

		onMainDrawerOpenWithChevron(label: string) {
			GTag.event("Detail Drawer Interaction", { event_category: "Builder", event_label: label });
		},
		
		sendDebouncedSearchEvent: debounce((entity:SearchEntity, location:BuilderActionLocations) => GTag.event(location, { event_category: "Builder", event_label: `Search ${entity}` }), 5000),

		onSearch(entity: SearchEntity, location:BuilderActionLocations) {
			EventTracking.Builder.sendDebouncedSearchEvent(entity, location)
		},

		onCopy(entity: SearchEntity, location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: `Copy ${entity}` });
		},

		onOpenSettings(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Settings" });
		},

		onOpenWritingLogs(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Open Writing Logs" });
		},

		onDeleteWritingLogs(location:BuilderActionLocations) {
			GTag.event(location, { event_category: "Builder", event_label: "Deleted Writing Log" });
		},

		onAddSlide(slideCount: number) {
			GTag.event("Slide Created", { event_category: "Builder", event_label: `Slide ${slideCount.toString()}` });
			TelemetryService.recordSlideChange();
		},

		onExtendSlide(slideCount: number) {
			GTag.event("Slide Extended", { event_category: "Builder", event_label: `Slide ${slideCount.toString()}` });
			TelemetryService.recordSlideChange();
		},

		onRemoveSlide(slideCount: number) {
			GTag.event("Slide Removed", { event_category: "Builder", event_label: `Slide ${slideCount.toString()}` });
			TelemetryService.recordSlideChange();
		},

		onRemoveSlideFromDrawer() {
			GTag.event("Slide Drawer Interaction", { event_category: "Builder", event_label: "Slide Deleted" });
		},

		onStartedTextToSpeech(seconds: number, words: number) {
			GTag.event("Audio Playback", { event_category: "Builder", event_label: `Started - ${seconds} seconds, ${words} words` });
		},

		onCompletedTextToSpeech(seconds: number, words: number) {
			GTag.event("Audio Playback", { event_category: "Builder", event_label: `Completed - ${seconds} seconds, ${words} words` });
		},

		onPausedTextToSpeech() {
			GTag.event("Audio Playback", { event_category: "Builder", event_label: "Paused" });
		},

		onUnPausedTextToSpeech() {
			GTag.event("Audio Playback", { event_category: "Builder", event_label: "Unpause" });
		},

		onReStartTextToSpeech() {
			GTag.event("Audio Playback", { event_category: "Builder", event_label: "Restart" });
		},

		onChangeTextToSpeechSpeed(speed: number) {
			GTag.event("Audio Settings", { event_category: "Builder", event_label: `Speed - ${speed}x` });
		},

		onChangeTextToSpeechVoice(voice: string) {
			GTag.event("Audio Settings", { event_category: "Builder", event_label: `Voice - ${voice}` });
		},
	};
}
